import { createSelector } from '@app/utils/createSelector'
import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'
import { resolvePromiseWithResolvers } from '@app/utils/resolvePromiseWithResolvers'

import { dispose } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'

import { setFirebaseDatabaseManager } from '@app/store/actions/contexts/firebaseDatabaseManager'
import { setFirebaseMessagingManager } from '@app/store/actions/contexts/firebaseMessagingManager'
import { configSelector } from '@app/store/slices/config'
import { profileUserSelector } from '@app/store/slices/profile'
import { isAuthenticatedSelector } from '@app/store/slices/session'
import type { Store } from '@app/store/store'
import { storeMonitoring } from '@app/store/store'

import { type FirebaseDatabaseManager } from './FirebaseDatabaseManager'
import { type FirebaseManager } from './FirebaseManager'
import { type FirebaseMessagingManager } from './FirebaseMessagingManager'

export class FirebaseLoader {
  private firebaseDatabaseManagerChannel = Promise.withResolvers<FirebaseDatabaseManager | null>()
  private firebaseMessagingManagerChannel = Promise.withResolvers<FirebaseMessagingManager | null>()
  private disposable: Disposable | null = null

  constructor(store: Store) {
    store.dispatch(setFirebaseDatabaseManager(this.firebaseDatabaseManagerChannel.promise))
    store.dispatch(setFirebaseMessagingManager(this.firebaseMessagingManagerChannel.promise))

    this.disposable = this.setup(store)
  }

  private setup(store: Store) {
    return spawn(async stack => {
      const state = store.getState()
      const { isPolygon } = configSelector(state)
      if (isPolygon) {
        this.firebaseDatabaseManagerChannel.resolve(null)
        return
      }

      const firebaseUserSelector = createSelector(
        [profileUserSelector, isAuthenticatedSelector],
        (user, isAuthenticated) => {
          return isAuthenticated && user && user.account_type !== 'visitor' ? { id: user.id, account_type: user.account_type } : null
        },
        {
          cache: {},
          resultEqual(a, b) {
            return a?.id === b?.id && a?.account_type === b?.account_type
          },
        }
      )

      let firebaseDispose: Disposable | null = null
      stack.defer(() => {
        if (firebaseDispose) {
          dispose(firebaseDispose)
        }
      })

      stack.use(
        storeMonitoring(store, firebaseUserSelector, async (user, _prevUser, signal) => {
          if (firebaseDispose) {
            dispose(firebaseDispose)
            firebaseDispose = null
          }
          if (!user) return
          firebaseDispose = spawn(async stack => {
            const { FirebaseManager } = await import('@app/services/FirebaseManager')
            signal.throwIfAborted()
            const firebaseManager = stack.use(new FirebaseManager(user, configSelector(state).firebase))

            stack.defer(() => {
              this.firebaseDatabaseManagerChannel = Promise.withResolvers()
            })
            stack.use(this.setupDatabase(firebaseManager, store, signal))

            stack.defer(() => {
              this.firebaseMessagingManagerChannel = Promise.withResolvers()
            })
            stack.use(this.setupMessaging(firebaseManager))
          }, reportSpawnFailure('FirebaseLoader'))
        })
      )
    })
  }

  private setupDatabase(firebaseManager: FirebaseManager, store: Store, signal: AbortSignal) {
    return spawn(async stack => {
      await resolvePromiseWithResolvers(this.firebaseDatabaseManagerChannel, async () => {
        const { FirebaseDatabaseManager } = await import('@app/services/FirebaseDatabaseManager')
        signal.throwIfAborted()
        return stack.use(new FirebaseDatabaseManager(firebaseManager, store))
      })
    }, reportSpawnFailure('FirebaseLoader'))
  }

  private setupMessaging(firebaseManager: FirebaseManager) {
    return spawn(async () => {
      await resolvePromiseWithResolvers(this.firebaseMessagingManagerChannel, async () => {
        const { FirebaseMessagingManager, FirebaseMessagingManagerUnsopportedError } = await import('@app/services/FirebaseMessagingManager')
        const m = new FirebaseMessagingManager(firebaseManager)
        try {
          await m.connect()
        } catch (e) {
          if (e instanceof FirebaseMessagingManagerUnsopportedError) {
            return null
          }
          throw e
        }
        return m
      })
    }, reportSpawnFailure('FirebaseLoader'))
  }

  [Symbol.dispose]() {
    if (this.disposable) {
      dispose(this.disposable)
    }
  }
}
